import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { tasks } from "../../lib/api";
import { Modal } from "../shared/Modal";
import { Button } from "../shared/Button";
import { TaskFields, type TaskFieldsValue } from "./TaskFields";
import type { Task } from "../../lib/types";

interface Props {
  task: Task;
  onClose: () => void;
}

// The kanban response (TaskListItem) omits `prompt`, so seeding state from
// the prop alone shows the field blank even when one is saved. Fetch the
// full row first and only mount the form once it's loaded.
export function TaskEditModal({ task, onClose }: Props) {
  const full = useQuery({
    queryKey: ["task", task.id],
    queryFn: () => tasks.get(task.id),
  });

  if (!full.data) {
    return (
      <Modal size="xl" title={`Edit "${task.title}"`} onClose={onClose}>
        <div className="py-8 text-center text-small text-text-tertiary">
          {full.isError ? "Failed to load task." : "Loading…"}
        </div>
      </Modal>
    );
  }

  return <EditForm task={full.data} onClose={onClose} />;
}

function EditForm({ task, onClose }: Props) {
  const qc = useQueryClient();
  const [fields, setFields] = useState<TaskFieldsValue>({
    projectId: task.project_id,
    title: task.title,
    description: task.description ?? "",
    prompt: task.prompt ?? "",
    autonomy: task.autonomy_level,
    model: task.model ?? "",
    priority: task.priority ?? "",
    specMode: task.spec_mode,
    tier: task.tier ?? "normal",
    acceptanceCriteria: task.acceptance_criteria ?? "",
  });
  const save = useMutation({
    mutationFn: () =>
      tasks.update(task.id, {
        title: fields.title,
        description: fields.description || null,
        prompt: fields.prompt || null,
        autonomy_level: fields.autonomy || undefined,
        model: fields.model || undefined,
        priority: fields.priority || null,
        spec_mode: fields.specMode,
        tier: fields.tier,
        acceptance_criteria: fields.acceptanceCriteria || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["task", task.id] });
      qc.invalidateQueries({ queryKey: ["kanban"] });
      onClose();
    },
  });

  const canSubmit = fields.title && !save.isPending;

  return (
    <Modal
      size="lg"
      title={`Edit "${task.title}"`}
      onClose={onClose}
      footer={
        <>
          <div className="flex-1" />
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
        </>
      }
    >
      <div>
        <TaskFields
          value={fields}
          onChange={(patch) => setFields((f) => ({ ...f, ...patch }))}
          projectLocked
          agentType={task.agent_type}
        />
        {save.isError && (
          <div className="mt-3 text-small text-status-error">
            {String(
              (save.error as { message?: string }).message ??
                "Failed to save task",
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
